import React, { useEffect, useState, useContext, useCallback } from "react";
import { Routes, Route, useParams, useLocation, useNavigate } from "react-router-dom";
import { fetchApi, fetchOrganization } from "services/APIRequest";
import { OrganizationContext } from "context/OrganizationContext";
import { dynamicModules } from "config/dynamicModules";
import { SiteProvider } from "context/SiteContext";
import { ThemeContext } from "context/ThemeContext";
import { LoggerContext } from "context/LoggerContext";
import { organizationUniqueUrl, siteUniqueUrl } from "config/urlConfig";
import { loadFonts } from "helpers/fontHelpers";
import MetaManager from "components/MetaManager";

import Navigation from "components/Navigation/Navigation";
import DynamicPage from "./DynamicPage";
import NotFound from "./NotFound";
import Loading from "./Loading";

/**
 * Checks the route and finds the appropriate content to render based on path analysis
 * 
 * @param {Object} props - Component props
 * @param {Array} props.pages - Available pages to search through
 * @param {Object} props.moduleType - Module type configuration for specialized routing
 * @returns {JSX.Element} The appropriate dynamic page or not found component
 */
const DynamicRouteChecker = ({ pages, moduleType }) => {
	const location = useLocation();
	const { org_url, site_url } = useParams();
	const { logDebug } = useContext(LoggerContext);

	// First analyze the full path before extracting relative path
	const fullPath = location.pathname;
	logDebug("DynamicRouteChecker analyzing full path", { fullPath, org_url, site_url });
	
	// Check for module patterns in the full path - solves deep nesting issues
	const segments = fullPath.split('/').filter(Boolean);
	
	// Extract all module patterns we need to detect
	const moduleRoutes = dynamicModules.map(dm => dm.route);
	
	// Scan the full path for any module patterns
	for (let i = 0; i < segments.length - 1; i++) {
		const segment = segments[i];
		
		// Check if this segment is a known module route
		if (moduleRoutes.includes(segment)) {
			
			// The last segment should be the uniqueUrlParam
			const uniqueUrlParam = segments[segments.length - 1];
			
			logDebug("DynamicRouteChecker found module in full path analysis", {
				moduleRoute: segment,
				position: i,
				fullSegments: segments,
				uniqueUrlParam
			});
			
			return (
				<DynamicPage 
					pageRoute={segment}
					uniqueUrlParam={uniqueUrlParam}
				/>
			);
		}
	}
	
	// If no module was found in the full path, continue with relative path analysis
	let relativePath = location.pathname;
	if (org_url && site_url) {
		relativePath = relativePath.replace(`/${org_url}/${site_url}/`, "");
	} else if (org_url) {
		relativePath = relativePath.replace(`/${org_url}/`, "");
	}
	relativePath = relativePath.replace(/^\/|\/$/g, "");
	
	logDebug("DynamicRouteChecker analyzing relative path", { relativePath, org_url, site_url });

	// If we know this is a moduleType request (e.g. a form)
	if (moduleType) {
		if (moduleType.isStaticPage) {
			return (
				<DynamicPage 
					pageRoute={moduleType.module} 
					isModuleRoute={true} 
					moduleType={moduleType} 
				/>
			);
		}
		
		const segments = relativePath.split('/');
		const uniqueUrlParam = segments[segments.length - 1] || relativePath;
		return (
			<DynamicPage 
				pageRoute={moduleType.module} 
				uniqueUrlParam={uniqueUrlParam} 
				isModuleRoute={true} 
			/>
		);
	}
	
	// Search for module pattern in the relative path segments
	const pathSegments = relativePath.split('/');
	
	// Check for known module patterns in the relative path
	for (let i = 0; i < pathSegments.length - 1; i++) {
		const currentSegment = pathSegments[i];
		const moduleMatch = dynamicModules.find(dm => dm.route === currentSegment);
		
		if (moduleMatch) {
			// Take the last segment as uniqueUrlParam
			const uniqueUrlParam = pathSegments[pathSegments.length - 1];
			
			logDebug("Found module match in relative path segment", { 
				segmentIndex: i,
				segment: currentSegment,
				module: moduleMatch.route, 
				uniqueUrl: uniqueUrlParam 
			});
			
			return (
				<DynamicPage 
					pageRoute={moduleMatch.route} 
					uniqueUrlParam={uniqueUrlParam} 
				/>
			);
		}
	}
	
	// Existing first segment check - keep as a fallback
	const firstSegment = pathSegments[0];
	const moduleMatch = dynamicModules.find(dm => dm.route === firstSegment);
	
	if (moduleMatch && pathSegments.length > 1) {
		const uniqueUrlParam = pathSegments[pathSegments.length - 1];
		logDebug("Found module match with uniqueUrl", { 
			module: moduleMatch.route, 
			uniqueUrl: uniqueUrlParam 
		});
		
		return (
			<DynamicPage 
				pageRoute={moduleMatch.route} 
				uniqueUrlParam={uniqueUrlParam} 
			/>
		);
	}

	// The rest of the matching logic remains unchanged
	const matchingPage = pages?.find((page) => {
		const candidate = page.route ? `${page.route}/${page.unique_url || ""}`.replace(/\/$/, "") : (page.unique_url || "").replace(/\/$/, "");
		return candidate && relativePath.startsWith(candidate);
	});

	if (matchingPage) {
		return <DynamicPage pageRoute={matchingPage.route} />;
	} else {
		const fallbackModule = dynamicModules.find((dm) => dm.route === firstSegment);
		if (fallbackModule) {
			return <DynamicPage pageRoute={fallbackModule.route} />;
		}
		return <NotFound />;
	}
};

/**
 * Dynamic content component that handles different routing modes:
 * - Standard mode with organization and site routing
 * - Module subdomain mode for dedicated module experiences
 * - Path module mode for local development of modules
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isModuleRoute - Whether this is a module-specific route
 * @param {Object} props.moduleType - Module type configuration for subdomain mode
 * @param {boolean} props.usePathModule - Whether to use path-based module routing (dev mode)
 * @param {Object} props.pathModuleType - Path module type configuration
 * @param {boolean} props.showListView - Whether to display a list view
 * @returns {JSX.Element} The rendered dynamic content
 */
const DynamicContent = ({ isModuleRoute, moduleType, usePathModule, pathModuleType, showListView }) => {
	const params = useParams(); // provides org_url and site_url
	const navigate = useNavigate();
	const location = useLocation();
	const { logDebug, logError } = useContext(LoggerContext);
	
	// Extract org_url directly from path for subdomains
	let orgUrlFromPath = "";
	
	if (isModuleRoute || usePathModule) {
		const pathSegments = location.pathname.split('/').filter(Boolean);
		orgUrlFromPath = pathSegments[0] || "";
		logDebug("Extracted organization from path", { 
			orgUrlFromPath, 
			fullPath: location.pathname,
			isModuleRoute,
			usePathModule,
			moduleType: moduleType?.module || pathModuleType?.module
		});
	}
	
	// Use config values if available, otherwise from useParams() or path
	const effectiveOrgUrl = organizationUniqueUrl || params?.org_url || orgUrlFromPath;
	const effectiveSiteUrl = siteUniqueUrl || params?.site_url;
	
	const [organization, setOrganization] = useState(null);
	const [site, setSite] = useState({ pages: [] });
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [faviconUrl, setFaviconUrl] = useState(null);

	const { updateTheme } = useContext(ThemeContext);
	
	// Check if we need to redirect (if redirectPath exists and no unique_url)
	const handleRedirectIfNeeded = useCallback(() => {
		// For static pages (isStaticPage) don't perform redirection
		if (usePathModule && pathModuleType && 
			pathModuleType.isStaticPage === true) {
			// Static pages should NEVER redirect
			logDebug("Static page detected, skipping redirect check", {
				module: pathModuleType?.module,
				isStaticPage: pathModuleType?.isStaticPage
			});
			return false;
		}
	
		if (usePathModule && pathModuleType && 
			!pathModuleType.isStaticPage && 
			pathModuleType.redirectPath && 
			!params?.unique_url) {
			// If on the organization page and redirectPath exists, redirect
			const redirectUrl = `/${effectiveOrgUrl}/${pathModuleType.redirectPath}`;
			logDebug(`Redirecting to: ${redirectUrl}`);
			navigate(redirectUrl, { replace: true });
			return true;
		}
		return false;
	}, [usePathModule, pathModuleType, params?.unique_url, effectiveOrgUrl, navigate, logDebug]);

	// First load Organization
	useEffect(() => {
		// Immediately check if we need to redirect
		if (handleRedirectIfNeeded()) {
			return; // If a redirect occurs, don't continue loading
		}
		
		logDebug("Loading organization with URL:", { 
			effectiveOrgUrl,
			orgUrlFromPath,
			paramsOrgUrl: params?.org_url,
			configOrgUrl: organizationUniqueUrl
		});
		
		if (!effectiveOrgUrl) {
			setError("Organization URL missing");
			setLoading(false);
			return;
		}

		const setStyle = (name, value, priotity) => {
			// Works because == (loose equality) treats null and undefined as equal
			if (name == null || value == null) {
				return false;
			} else {
				return document.documentElement.style.setProperty(name, value, priotity);
			}
		};
		
		const loadOrganization = async () => {
			try {
				let orgData = await fetchOrganization(effectiveOrgUrl);
				orgData = orgData.data;
				logDebug("Loaded organization:", orgData);
				setOrganization(orgData);

				// Set theme initially if ci is available
				if (orgData?.ci) {
					const hexToRgb = (hex) => {
						let r = 0,
							g = 0,
							b = 0;
						if (hex.length === 4) {
							r = parseInt(hex[1] + hex[1], 16);
							g = parseInt(hex[2] + hex[2], 16);
							b = parseInt(hex[3] + hex[3], 16);
						} else if (hex.length === 7) {
							r = parseInt(hex[1] + hex[2], 16);
							g = parseInt(hex[3] + hex[4], 16);
							b = parseInt(hex[5] + hex[6], 16);
						}
						return `${r}, ${g}, ${b}`;
					};

					// Load all required fonts before applying them
					const fontsToLoad = [];
					if (orgData?.ci?.font) {
						fontsToLoad.push(orgData.ci.font);
					}
					if (orgData?.ci?.font_headline) {
						fontsToLoad.push(orgData.ci.font_headline);
					}
					if (orgData?.ci?.font_subline) {
						fontsToLoad.push(orgData.ci.font_subline);
					}
					if (orgData?.ci?.font_floattext) {
						fontsToLoad.push(orgData.ci.font_floattext);
					}
					
					// Fallback to legacy string fonts if no objects are available
					if (fontsToLoad.length === 0) {
						const legacyFonts = [
							orgData?.ci?.font_family_headline,
							orgData?.ci?.font_family_subline,
							orgData?.ci?.font_family_floattext
						].filter(Boolean);
						if (legacyFonts.length > 0) {
							logDebug("Loading legacy fonts:", legacyFonts);
							await loadFonts(legacyFonts, logDebug);
						}
					} else {
						logDebug("Loading fonts with object format:", fontsToLoad.map(font => font.title));
						await loadFonts(fontsToLoad, logDebug);
					}

					// Dynamically update theme variables
					if (orgData?.ci?.primary_color) {
						logDebug("Applying primary color:", orgData?.ci?.primary_color);
						
						// Set CSS variables directly (in addition to ThemeContext)
						
						setStyle('--primary', orgData.ci.primary_color);
						setStyle('--primary-rgb', hexToRgb(orgData.ci.primary_color));
						setStyle('--bs-primary', orgData.ci.primary_color);
						setStyle('--bs-primary-rgb', hexToRgb(orgData.ci.primary_color));
						
						setStyle('--font-primary', orgData.ci.font_headline?.title || orgData.ci.font?.title || orgData.ci.font_family_headline);
						
						setStyle('--font-size-headline', orgData.ci.font_size_headline);

						setStyle('--font-secondary', orgData.ci.font_subline?.title || orgData.ci.font_family_subline);

						setStyle('--font-size-subline', orgData.ci.font_size_subline || "1");

						setStyle('--font-tertiary', orgData.ci.font_floattext?.title || orgData.ci.font_family_floattext);

						setStyle('--font-size-floattext', orgData.ci.font_size_floattext);
						
						
						// Also insert into ThemeContext
						updateTheme({
							...(orgData?.ci?.primary_color && {
								"primary": orgData?.ci?.primary_color,
								"primary-rgb": hexToRgb(orgData?.ci?.primary_color),
								"bs-primary": orgData?.ci?.primary_color,
								"bs-primary-rgb": hexToRgb(orgData?.ci?.primary_color),
							}),
							...(orgData?.ci?.primary_color_dark && {
								"primary-dark": orgData?.ci?.primary_color_dark,
								"primary-dark-rgb": hexToRgb(orgData?.ci?.primary_color_dark),
							}),
							...(orgData?.ci?.font_headline?.title || orgData?.ci?.font?.title || orgData?.ci?.font_family_headline ? {
								"font-primary": orgData?.ci?.font_headline?.title || orgData?.ci?.font?.title || orgData?.ci?.font_family_headline,
							} : {}),
							...(orgData?.ci?.font_subline?.title || orgData?.ci?.font_family_subline ? {
								"font-secondary": orgData?.ci?.font_subline?.title || orgData?.ci?.font_family_subline,
							} : {}),
							...(orgData?.ci?.font_floattext?.title || orgData?.ci?.font_family_floattext ? {
								"font-tertiary": orgData?.ci?.font_floattext?.title || orgData?.ci?.font_family_floattext,
							} : {}),
							...(orgData?.ci?.font_size_headline && {
								"font-size-headline": `${orgData?.ci?.font_size_headline}rem`,
							}),
							...(orgData?.ci?.font_size_subline && {
								"font-size-subline": `${orgData?.ci?.font_size_subline}rem`,
							}),
							...(orgData?.ci?.font_size_floattext && {
								"font-size-floattext": `${orgData?.ci?.font_size_floattext}rem`,
							}),
						});
					}
					if (orgData?.ci?.secondary_color) {
						logDebug("Applying secondary color:", orgData?.ci?.secondary_color);
						
						// Set CSS variables directly
						setStyle('--secondary', orgData?.ci?.secondary_color);
						setStyle('--secondary-rgb', hexToRgb(orgData?.ci?.secondary_color));
						setStyle('--bs-secondary', orgData?.ci?.secondary_color);
						setStyle('--bs-secondary-rgb', hexToRgb(orgData?.ci?.secondary_color));
						
						updateTheme({
							"secondary": orgData?.ci?.secondary_color,
							"secondary-rgb": hexToRgb(orgData?.ci?.secondary_color),
							"bs-secondary": orgData?.ci?.secondary_color,
							"bs-secondary-rgb": hexToRgb(orgData?.ci?.secondary_color),
							"secondary-dark": orgData?.ci?.secondary_color_dark || orgData?.ci?.secondary_color,
							"secondary-dark-rgb": hexToRgb(orgData?.ci?.secondary_color_dark || orgData?.ci?.secondary_color),
						});
					}
					
					// Also update button styles directly
					if (orgData?.ci?.primary_color) {
						const btnPrimaryStyle = `
							.btn-primary {
								--bs-btn-bg: ${orgData?.ci?.primary_color};
								--bs-btn-border-color: ${orgData?.ci?.primary_color};
								background-color: ${orgData?.ci?.primary_color};
								border-color: ${orgData?.ci?.primary_color};
							}
						`;
						
						// Add a style tag or update an existing one
						let styleEl = document.getElementById('dynamic-theme-style');
						if (!styleEl) {
							styleEl = document.createElement('style');
							styleEl.id = 'dynamic-theme-style';
							document.head.appendChild(styleEl);
						}
						styleEl.textContent = btnPrimaryStyle;
						
						logDebug("Applied custom button styles");
					}
					if (orgData?.ci?.favicon && Array.isArray(orgData?.ci?.favicon) && orgData?.ci?.favicon.length > 0) {
						setFaviconUrl(orgData?.ci?.favicon[0]?.mediaUrl);
					}
				}

				// Different handling depending on routing type
				if (isModuleRoute && moduleType) {
					// Subdomain mode (e.g. forms.canou.io/engbers-bau)
					logDebug("Subdomain mode activated for:", moduleType);
					const moduleSite = {
						name: `${moduleType?.module} - ${orgData?.name}`,
						navigation: false, // No navigation menu on module subdomains
						pages: [
							{
								route: moduleType?.module,
								id: moduleType?.moduleId
							}
						]
					};
					setSite(moduleSite);
				} 
				else if (usePathModule && pathModuleType) {
					// Direct path mode (e.g. localhost/engbers-bau/formular-id)
					logDebug("Path module mode activated for:", pathModuleType);
					
					// Get the current path segments
					const pathSegments = location.pathname.split('/').filter(Boolean);
					
					// Check if we are on the organization root (only org segment, no module uniqueUrl)
					const isOrgRoot = pathSegments.length === 1 && pathSegments[0] === effectiveOrgUrl;
					// Check if we're on detail page (has more than one segment and not empty)
					const isDetailView = pathSegments.length > 1 && pathSegments[1] !== '';
					// Check if we need list view
					const needsListView = isOrgRoot || (pathSegments.length === 2 && pathSegments[1] === '');
					
					// New: Get uniqueUrl for detail view
					const uniqueUrl = isDetailView ? pathSegments[1] : null;
					
					logDebug("Path module path analysis:", {
						pathSegments,
						effectiveOrgUrl,
						isOrgRoot,
						isDetailView, 
						uniqueUrl,
						needsListView,
						pathModuleType: pathModuleType?.module,
						hasListView: pathModuleType?.hasListView
					});
					
					const moduleSite = {
						name: `${pathModuleType?.module} - ${orgData?.name}`,
						navigation: false, // No navigation menu
						pages: [
							{
								route: pathModuleType?.module,
								id: pathModuleType?.moduleId
							}
						]
					};
					
					// For detail view, we don't need a start page as we'll directly render the detail page
					if (isDetailView) {
						// Do not set start_page for detail views
						logDebug("Detail view detected, not creating start page");
					}
					// Create a dummy start page for list views or static pages
					else if (pathModuleType?.isStaticPage || needsListView) {
						logDebug("Creating start page for module", {
							isStaticPage: pathModuleType?.isStaticPage,
							needsListView,
							module: pathModuleType?.module
						});
						
						// Access the module configuration from dynamicModules
						const moduleConfig = dynamicModules.find(m => m.module_id === pathModuleType?.moduleId);
						const moduleTitle = pathModuleType?.module.charAt(0).toUpperCase() + pathModuleType?.module.slice(1);
						
						if (needsListView && pathModuleType?.hasListView) {
							// Create a list view start page
							moduleSite.start_page = {
								title: moduleTitle,
								sections: [
									{
										global_type: { id: 95, name: "BreadcrumbsSection" },
										title: moduleTitle,
										hideOnModuleSubdomain: true
									},
									{
										global_type: { id: 52, name: "CardsListSection" },
										title: moduleTitle,
										shouldLoadOwnData: true,
										// Use API endpoint from dynamicModules config
										endpoint: moduleConfig?.apiMapping,
										module_id: pathModuleType?.moduleId,
										items_per_page: 10
									}
								]
							};
						} else if (pathModuleType?.isStaticPage) {
							// Create a static page start page
							moduleSite.start_page = {
								title: moduleTitle,
								sections: [
									{
										global_type: { id: 95, name: "BreadcrumbsSection" },
										title: moduleTitle,
										hideOnModuleSubdomain: true
									}
								]
							};
						}
					}
					
					setSite(moduleSite);
				}
				// If site_url is present and not a module subdomain, load the specific site
				else if (effectiveSiteUrl) {
					try {
						const siteData = await fetchApi(`sites/public/${effectiveSiteUrl}`, orgData?.otk);
						setSite(siteData?.data);
					} catch (siteErr) {
						logError(`Error loading site ${effectiveSiteUrl}:`, siteErr);
						setSite({ 
							name: `${effectiveSiteUrl} (Error loading)`, 
							pages: [] 
						});
					}
				} else {
					// Otherwise load the organization's default site
					try {
						const defaultSite = await fetchApi(`organizations/${effectiveOrgUrl}/default-site`, orgData?.otk);
						setSite(defaultSite);
					} catch (siteErr) {
						logError(`Error loading default site:`, siteErr);
						setSite({ 
							name: `${orgData?.name} (Error loading)`, 
							pages: [] 
						});
					}
				}
			} catch (err) {
				setError(err?.message);
				logError(`Error loading organization ${effectiveOrgUrl}:`, err);
			} finally {
				setLoading(false);
			}
		};

		if (effectiveOrgUrl) {
			loadOrganization();
		} else {
			setLoading(false);
		}
	}, [effectiveOrgUrl, effectiveSiteUrl, updateTheme, logDebug, logError, isModuleRoute, moduleType, usePathModule, pathModuleType, params, handleRedirectIfNeeded, orgUrlFromPath, location.pathname]);

	if (loading) return <Loading />;
	if (error) {
		if (error === "Page not found") {
			return <NotFound />;
		}
		return <p>Error: {error}</p>;
	}
	
	// Debug active subdomain route
	if (isModuleRoute && moduleType) {
		logDebug("Subdomain active routes:", {
			pathname: location.pathname,
			effectiveOrgUrl,
			isStaticPage: moduleType?.isStaticPage
		});
	}

	// For module subdomain we use special routing
	if (isModuleRoute && moduleType) {
		// IMPORTANT CHANGE: We extract not only from params, but also from the path
		let uniqueUrl = params?.unique_url || "";
		
		// If no uniqueUrl in parameter, try to extract from path
		if (!uniqueUrl && location.pathname) {
			const pathSegments = location.pathname.split('/').filter(Boolean);
			// The first part is always the organization
			// The second part (if present) is the uniqueUrl
			if (pathSegments.length > 1) {
				uniqueUrl = pathSegments[1];
			}
		}
		
		return (
			<OrganizationContext.Provider value={organization}>
				<MetaManager 
					favicon16={faviconUrl || `/assets/img/favicons_fallback/icon_16X16.png`}
					favicon32={faviconUrl || `/assets/img/favicons_fallback/icon_32X32.png`}
					themeColor={organization?.ci?.primary_color || '#ffffff'}
					title={`${moduleType?.module} - ${organization?.name}`}
				/>
				<SiteProvider site={site}>
					{site?.navigation && <Navigation />}
					<Routes>
						{/* For static pages always the same route without uniqueUrlParam */}
						{moduleType?.isStaticPage ? (
							<Route 
								path="*" 
								element={
									<DynamicPage 
										pageRoute={moduleType?.module} 
										isModuleRoute={true}
										organizationOverride={organization}
										orgUrlOverride={effectiveOrgUrl}
									/>
								} 
							/>
						) : (
							<>
								{/* For regular modules with uniqueUrl: Detail view */}
								{uniqueUrl && (
									<Route
										path="*"
										element={
											<DynamicPage 
												pageRoute={moduleType?.module} 
												uniqueUrlParam={uniqueUrl} 
												isModuleRoute={true} 
											/>
										}
									/>
								)}
								
								{/* For regular modules without uniqueUrl: List view or NotFound */}
								{!uniqueUrl && (
									moduleType?.hasListView ? (
										<Route
											path="*"
											element={
												<DynamicPage 
													pageRoute={moduleType?.module} 
													isModuleRoute={true} 
													showListView={true} 
												/>
											}
										/>
									) : (
										<Route path="*" element={<NotFound />} />
									)
								)}
							</>
						)}
					</Routes>
				</SiteProvider>
			</OrganizationContext.Provider>
		);
	}

	// Path module mode routing - CRITICAL FIX FOR DETAIL VIEW AND STATIC PAGES
	if (usePathModule && pathModuleType) {
		// Check for detail view URL pattern
		const pathSegments = location.pathname.split('/').filter(Boolean);
		const isDetailView = pathSegments.length > 1 && pathSegments[1] !== '';
		const detailUniqueUrl = isDetailView ? pathSegments[1] : null;
		
		logDebug("Path module rendering with full details", {
			pathname: location.pathname,
			module: pathModuleType?.module,
			orgUrl: effectiveOrgUrl,
			hasStartPage: !!site?.start_page,
			isDetailView,
			detailUniqueUrl,
			pathSegments,
			params: JSON.stringify(params),
			isStaticPage: pathModuleType?.isStaticPage
		});

		// For detail views OR STATIC PAGES, bypass the router completely
		if (isDetailView || pathModuleType?.isStaticPage) {
			return (
				<OrganizationContext.Provider value={organization}>
					<MetaManager 
						favicon16={faviconUrl || `/assets/img/favicons_fallback/icon_16X16.png`}
						favicon32={faviconUrl || `/assets/img/favicons_fallback/icon_32X32.png`}
						themeColor={organization?.ci?.primary_color || '#ffffff'}
						title={`${pathModuleType?.module} - ${organization?.name}`}
					/>
					<SiteProvider site={site}>
						{site?.navigation && <Navigation />}
						<DynamicPage 
							pageRoute={pathModuleType?.module}
							uniqueUrlParam={isDetailView ? detailUniqueUrl : null}
							usePathModule={true}
							organizationOverride={organization}
							orgUrlOverride={effectiveOrgUrl}
						/>
					</SiteProvider>
				</OrganizationContext.Provider>
			);
		}

		// Only use Router for non-detail views of dynamic modules (e.g. list views)
		return (
			<OrganizationContext.Provider value={organization}>
				<MetaManager 
					favicon16={faviconUrl || `/assets/img/favicons_fallback/icon_16X16.png`}
					favicon32={faviconUrl || `/assets/img/favicons_fallback/icon_32X32.png`}
					themeColor={organization?.ci?.primary_color || '#ffffff'}
					title={`${pathModuleType?.module} - ${organization?.name}`}
				/>
				<SiteProvider site={site}>
					{site?.navigation && <Navigation />}
					<Routes>
						{/* Index route for the root path (organization page) */}
						<Route
							index
							element={
								site?.start_page ? (
									<DynamicPage 
										isStartPage={true}
										startPage={site.start_page}
										organizationOverride={organization}
										orgUrlOverride={effectiveOrgUrl}
									/>
								) : (
									<div>No start page configured for module {pathModuleType?.module}</div>
								)
							}
						/>
						
						{/* Catch-all route for any other paths that aren't detail views */}
						<Route
							path="*"
							element={
								<DynamicPage 
									pageRoute={pathModuleType?.module}
									usePathModule={true}
									organizationOverride={organization}
									orgUrlOverride={effectiveOrgUrl}
								/>
							}
						/>
					</Routes>
				</SiteProvider>
			</OrganizationContext.Provider>
		);
	}

	// Standard rendering for normal pages
	return (
		<OrganizationContext.Provider value={organization}>
			<MetaManager 
				favicon16={faviconUrl || `/assets/img/favicons_fallback/icon_16X16.png`}
				favicon32={faviconUrl || `/assets/img/favicons_fallback/icon_32X32.png`}
				themeColor={organization?.ci?.primary_color || '#ffffff'}
			/>
			<SiteProvider site={site}>
				{site?.navigation && <Navigation />}
				<Routes>
					{/* Dynamic Routes */}
					{site?.pages?.map((page) => {
						if (page?.unique_url) {
							// Detail pages: e.g. /stellen/tiefbauer-mwd/*
							const path = page.route ? `${page.route}/${page.unique_url}/*` : `${page.unique_url}/*`;
							return (
								<Route
									key={page.id ?? page.unique_url}
									path={path}
									element={<DynamicPage pageRoute={page.route} />}
								/>
							);
						} else {
							// Index page of the module: e.g. /stellen and additionally /stellen/* for possible subpages
							return (
								<React.Fragment key={page.id ?? page.route}>
									<Route
										path={page.route}
										element={<DynamicPage pageRoute={page.route} />}
									/>
									<Route
										path={`${page.route}/*`}
										element={<DynamicPage pageRoute={page.route} />}
									/>
								</React.Fragment>
							);
						}
					})}

					{/* Then fixed routes */}
					<Route
						path="checkout"
						element={<DynamicPage pageRoute="checkout" />}
					/>
					<Route
						path="impressum"
						element={<DynamicPage pageRoute="impressum" />}
					/>
					<Route
						path="datenschutz"
						element={<DynamicPage pageRoute="datenschutz" />}
					/>
					<Route
						path="feedback"
						element={<DynamicPage pageRoute="feedback" />}
					/>

					{/* Index Route for start page */}
					<Route
						index
						element={
							site?.start_page ? (
								<DynamicPage
									isStartPage={true}
									startPage={site.start_page}
								/>
							) : (
								<div>No start page configured</div>
							)
						}
					/>

					{/* Route for direct page URLs (e.g., /synagogenbrand) - matches single segment */}
					<Route
						path=":page_url"
						element={<DynamicPage />}
					/>

					{/* Route for multi-segment URLs that don't match site pages */}
					<Route
						path="*"
						element={
							<DynamicRouteChecker 
								pages={site?.pages} 
								moduleType={moduleType} 
							/>
						}
					/>
				</Routes>
			</SiteProvider>
		</OrganizationContext.Provider>
	);
};

export default DynamicContent;
